import {Injectable} from '@angular/core';
import {
    Color,
    Geometry, LinearFilter,
    Mesh,
    MeshBasicMaterial, NearestFilter,
    Object3D, Scene,
    Vector2,
    Vector3, WebGLRenderTarget,
} from 'three';
import {ContainerService} from '../../Utils/container.service';
import {RenderService} from '../Scene/render.service';
import {CameraService} from '../Scene/camera.service';
import {ColorConverter} from './color-converter';
import {Labels} from './labels';


@Injectable({
    providedIn: 'root'
})
export class ColorCodingService {

    // get/set
    private _resFactor: number;
    private _rgbColorStep = 16;

    private _colorConverter = new ColorConverter();
    private _pixelData: any;
    private _pixelHelpData: any;

    private _bufferScene = new Scene();
    private _buffer: WebGLRenderTarget;

    private readonly _colors: Labels;

    constructor(private containerService: ContainerService,
                private renderService: RenderService,
                private cameraService: CameraService) {
        this._colors = new Labels();
        // this.containerService.paintColor = this._colors.color2b;
    }

    get resFactor(): number {
        return this._resFactor;
    }

    set resFactor(value: number) {
        this._resFactor = value;
    }

    get color2a(): Color {
        return this._colors.color2a.color;
    }

    get color2b(): Color {
        return this._colors.color2b.color;
    }

    get color3(): Color {
        return this._colors.color3.color;
    }

    get color4(): Color {
        return this._colors.color4.color;
    }

    get colorSkin() {
        return this._colors.colorSkin;
    }

    /**
     *returns all possible drawing colors
     */
    get colors(): Labels {
        return this._colors;
    }

    get bufferScene(): Scene {
        return this._bufferScene;
    }

    /**
     * iterates over all polygons and colors them in a unique color (depending on the index)
     * @param geometry
     */
    public initColors(geometry: Geometry) {
        geometry.elementsNeedUpdate = true;
        for (let i = 0; i < geometry.faces.length; i++) {
            const color = new Color();
            const c: Color = this._colorConverter.transformIndex(i);
            color.setRGB(c.r, c.g, c.b);

            geometry.faces[i].color = color;
            geometry.colorsNeedUpdate = true;
            geometry.verticesNeedUpdate = true;
            geometry.elementsNeedUpdate = true;

        }
    }

    /**
     * adds brush drawing object to the buffer scene
     */
    public initBufferSelectionArea() {
        this.containerService.width = this.containerService.canvasWidth * this.resFactor;
        this.containerService.height = this.containerService.canvasHeight * this.resFactor;
        this.setBuffer();
        this.clearScene(this._bufferScene);
        const modelMesh = this.containerService.lassoSelectionObject.clone(true);
        this._bufferScene.add(modelMesh);
    }

    /**
     * init Renderbuffer for texture Painting. Size of buffer is depending on camera distance
     * @param marginalfilter
     */
    public initBufferTexture(marginalfilter = false) {
        this.resFactor = this.getOptimalResFactor();
        this.containerService.width = this.containerService.canvasWidth * this.resFactor;
        this.containerService.height = this.containerService.canvasHeight * this.resFactor;
        this.setBuffer();
        // this.containerService.bufferScene = new Scene();
        this.clearScene(this._bufferScene);
        const modelMesh = <Mesh>this.containerService.renderGroup.children[0].clone(true);
        // const modelMesh = <THREE.Mesh>meshes.children[0];
        const textureGroup = new Object3D();
        textureGroup.add(modelMesh);
        const material = <MeshBasicMaterial>this.containerService.materialTexture;
        modelMesh.material = material;
        material.needsUpdate = true;
        this._bufferScene.add(textureGroup);
        if (marginalfilter) {
            this._bufferScene.add(this.containerService.helpPlane.clone(true));
        }

    }

    /**
     * init buffer for polypaint
     */
    public initBufferPoly() {
        this.resFactor = 1;
        this.containerService.width = this.containerService.canvasWidth * this.resFactor;
        this.containerService.height = this.containerService.canvasHeight * this.resFactor;
        this.setBuffer();
        this.clearScene(this._bufferScene);
        const modelMesh = <Mesh>this.containerService.polyPaintGroup.children[0].clone(true);
        // let box = new BoxBufferGeometry(100, 100, 100);
        this._bufferScene.add(modelMesh);
        // this.containerService.bufferScene.add(mesh);
    }

    /**
     * renders image for color coding to target
     * @param x: number
     * @param y: number
     * @param width: number
     * @param height: number
     * @param check: boolean (brush selection area or color coding)
     */
    public renderColorpaintImg(x: number, y: number, width: number, height: number, check: boolean) {
        // TODO apply a filter wich deletes all irrelevant values
        this.renderService.renderer.clear(true, true, true);
        this.renderService.renderer_colorCoding.setRenderTarget(this._buffer);
        this.renderService.renderer_colorCoding.render(this._bufferScene, this.cameraService.camera);
        const pixels = new Uint8Array((4 * (width - x) * (height - y)));

        this.renderService.renderer_colorCoding.readRenderTargetPixels(this._buffer,
            x,
            y, width - x, height - y, pixels);
        if (check) {
            // this._pixelData = [];
            this._pixelData = pixels;
        } else {
            this._pixelHelpData = pixels;
        }
    }

    /**
     * calculates color in buffer on given index
     * @param x
     * @param y
     * @param w
     * @returns texelColor: Color
     */
    public checkVisibilityoverColors(x, y, w): Color {
        let r: number;
        let g: number;
        let b: number;
        const i = y * w + x;

        // const pix = this.containerService.pixelData;

        if (this._pixelData[i] !== 255 || this._pixelData[i + 1] !== 255 || this._pixelData[i + 2] !== 255) {
            r = this._pixelData[i];
            g = this._pixelData[i + 1];
            b = this._pixelData[i + 2];
            return new Color(r, g, b);
        }
        return null;
    }

    /**
     * returns brush points (optical test if brush is set on given index)
     * @param x
     * @param y
     * @param w
     * @returns brushTexel: Color
     */
    public opticalBrushSelection(x, y, w): Color {

        let r: number;
        let g: number;
        let b: number;
        const i = y * w + x;
        if (this._pixelHelpData[i] !== 255 || this._pixelHelpData[i + 1] !== 255 || this._pixelHelpData[i + 2] !== 255) {
            r = this._pixelHelpData[i];
            g = this._pixelHelpData[i + 1];
            b = this._pixelHelpData[i + 2];

            return new Color(r / 255, g / 255, b / 255);

        }
        return null;
    }

    /**
     * checks if index is in the brush selection area
     * @param index
     * @ returns Color
     */
    public getBrushPoints(index: number): Color {

        let r: number;
        let g: number;
        let b: number;

        if (this._pixelData[index] !== 255 ||
            this._pixelData[index + 1] !== 255 ||
            this._pixelData[index + 2] !== 255) {

            r = this._pixelData[index];
            g = this._pixelData[index + 1];
            b = this._pixelData[index + 2];

            return new Color(r, g, b);

        }
    }

    /**
     * calculate all texel indices of the rendered buffer
     * every detected index is visible to the camera
     * @returns indexSet: Set<number>()
     */
    public getIndexindTexture(): Set<number> {
        const indexSet = new Set<number>();
        for (let i = 0; i < this._pixelHelpData.length; i += 4) {
            if (this._pixelHelpData[i] !== 0 || this._pixelHelpData[i + 1] !== 0 ||
                this._pixelHelpData[i + 1] !== 0) {
                const index = this.getColorIndexTexture(this.getBrushPoints(i));
                indexSet.add(index);
            }
        }
        return indexSet;
    }

    // **********************************************************
    /**
     * returns index of texel depending on given color
     * @param c: Color
     * @returns index: number
     */
    public getColorIndexTexture(c: Color): number {
        // return (c.r * 256 * 256 + c.g * 256 + c.b);
        return this._colorConverter.transformColor_Texture(c.r, c.g, c.b, this._rgbColorStep);
    }

    /**
     * returns a index depending on color (poly mode)
     * @param c
     * @returns index: number
     */
    public getColorIndexPoly(c: Color): number {
        return this._colorConverter.transformColor(c.r, c.g, c.b);
    }

    /**
     * calculate a check area depending on the lasso points
     * @param selPoints2d
     * @param offSet
     */
    public getCheckarea(selPoints2d, offSet: number) {
        const result: number[] = [this.cameraService.canvasWidth, this.cameraService.canvasHeight,
            0, 0, 0, 0, 0, 0];
        for (const sel of selPoints2d) {
            if (sel.x < result[0]) {
                result[0] = Math.floor(sel.x);
            }
            if (sel.y < result[1]) {
                result[1] = Math.floor(sel.y);
            }
            if (sel.x > result[2]) {
                result[2] = Math.ceil(sel.x);
            }
            if (sel.y > result[3]) {
                result[3] = Math.ceil(sel.y);
            }

        }

        result[4] = result[0];
        result[5] = result[1];
        result[0] = Math.round((this.containerService.width / 2) + result[0] * this.resFactor) - offSet;
        result[0] = result[0] > 0 ? result[0] : 0;
        result[1] = Math.round((this.containerService.height / 2) + result[1] * this.resFactor) - offSet;
        result[1] = result[1] > 0 ? result[1] : 0;
        result[2] = Math.round((this.containerService.width / 2) + result[2] * this.resFactor) + offSet;
        result[2] = result[2] < this.containerService.width ? result[2] : this.containerService.width - 1;
        result[3] = Math.round((this.containerService.height / 2) + result[3] * this.resFactor) + offSet;
        result[3] = result[3] < this.containerService.height ? result[3] : this.containerService.height - 1;

        return result;
    }

    /**
     * transforms coordinates
     * @param x
     * @param y
     * returns Vector2
     */
    public transformToCenterCoord(x, y): Vector2 {
        x = x / this.resFactor;
        y = y / this.resFactor;

        x = x - (this.cameraService.canvasWidth / 2);
        y = y - (this.cameraService.canvasHeight / 2);


        return new Vector2(x, y);
    }

    // -------------------------------------------------------------------------------------------------------------------------------------

    private setBuffer() {
        this._buffer = new WebGLRenderTarget(this.containerService.width, this.containerService.height);
        this._buffer.texture.minFilter = LinearFilter;
        this._buffer.texture.magFilter = NearestFilter;
    }

    /**
     * clears all from given Object3D (for bufferScene cleanUp)
     * @param obj
     */
    private clearScene(obj: Object3D) {
        while (obj.children.length > 0) {
            this.clearScene(obj.children[0]);
            obj.remove(obj.children[0]);
        }
    }

    /**
     * calculate resolution factor depending camera distance to the model
     */
    private getOptimalResFactor(): number {
        /*const dist = new Vector3(0, 0, 0).distanceTo(this.cameraService.camera.position);
        console.log(dist);
        if (dist > 200) {
            return Math.round(dist / 100);
        }*/
        // TODO make Buffer as big as possible for the specific device
        return 5;

    }
}
